import {combine, forward, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {InnerFilters, InnerSortings} from 'types';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    data$,
    defaultFilters,
    defaultSortings,
    fetchDataFiltersFx,
    fetchDataFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    pageNumber$,
    pageSize$,
    receiptFilters$,
    search$,
    searchChanged,
    searchReset,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateChanged,
} from './index';

data$.on(fetchDataFx.done, (state, {result}) => result).reset(Gate.close);
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, searchChanged);
pageSize$.reset(stateReset);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

interface CombinedGridFetchSource {
    sortings: InnerSortings;
    pageNumber: number;
    pageSize: number;
    filters: InnerFilters;
    search: string;
}

const gridFetchSource = combine({
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    search: search$,
});

const mapGridFetchParams = ({
    pageNumber,
    pageSize,
    filters,
    sortings,
    search,
}: CombinedGridFetchSource) => ({
    paging: {
        pageNumber,
        pageSize,
    },
    ...(search && {docOrOwner: search}),
    filters: mapFilters(filters),
    sortings: mapSortings(sortings),
});

forward({
    from: Gate.open,
    to: searchReset,
});

sample({
    clock: Gate.open,
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDataFiltersFx,
});

sample({
    clock: merge([Gate.open, tableStateChanged, searchChanged]),
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDataFx,
});

receiptFilters$.on(fetchDataFiltersFx.doneData, (_, result) => result).reset(Gate.close);

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
